import { queryClient } from '../../../ReactQuery';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';

/**
 * Parameters for updating release group report custom text.
 */
type UpdateReleaseGroupReportCustomTextParams = {
  /** The release group ID */
  releaseGroupId: number;
  /** The custom text content to save for the release group report */
  reportCustomText: string;
};

/**
 * Updates the custom text for a specific release group's report.
 *
 * @param params - The parameters for updating release group report custom text
 * @param params.releaseGroupId - The release group ID
 * @param params.reportCustomText - The custom text content to save
 * @returns Promise that resolves when the update is complete
 */
async function updateReleaseGroupReportCustomText({
  releaseGroupId,
  reportCustomText,
}: UpdateReleaseGroupReportCustomTextParams): Promise<void> {
  await put(`/api/project_group/${releaseGroupId}`, { reportCustomText });
}

/**
 * Hook for updating release group report custom text.
 *
 * This hook provides a mutation function to update the custom text content
 * for a specific release group's report. It handles success/error messaging and
 * invalidates relevant queries on success.
 *
 * @example
 * ```typescript
 * const { mutateAsync } = useUpdateReleaseGroupReportCustomText();
 *
 * await mutateAsync({
 *   releaseGroupId: 12345,
 *   reportCustomText: "Custom header text for this release group"
 * });
 * ```
 *
 * @returns A React Query mutation object with mutateAsync function
 */
export default function useUpdateReleaseGroupReportCustomText() {
  return useToastedMutation({
    mutationFn: updateReleaseGroupReportCustomText,
    successToastMessage: 'Release group report text updated successfully',
    errorToastMessage: 'Failed to update release group report text',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
  });
}
